/**
 * Demo-only "Killer-flow review functions" toggle card.
 *
 * Ronnie's specific ask: "what if a buyer only wants regulatory and
 * medical review, not the full brand/agency/regulatory/medical stack?"
 * This card lets a demoer toggle which reviewer functions appear in
 * the killer-flow stream before kicking off a review. The backend
 * filters `KILLER_FLOW_CANNED` against the saved set, so disabled
 * functions also drop out of the materialised ticket comments and
 * project stages.
 *
 * Custom reviewer types (Add/Remove/Rename) are user-added entries
 * that live alongside the 5 built-in defaults. They never override
 * the defaults — defaults are immutable identifiers (brand / agency /
 * regulatory / medical / copy) and can only be checked / unchecked
 * (hidden from the demo). Customs are full CRUD and are exposed so
 * the demoer can attribute scripted-response copy to their own
 * reviewer labels.
 *
 * Phase 1 of the 3-tier asset flow plan. MG superusers only — backend
 * gates on `UserService.hasMeaningfulGigsRole` + `isDemoEnterpriseId`.
 */
import { Pencil, Plus, Trash } from "@phosphor-icons/react";
import {
  Button,
  Card,
  Checkbox,
  Flex,
  Heading,
  IconButton,
  Separator,
  Text,
  TextField,
} from "@radix-ui/themes";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

import {
  DEMO_CUSTOM_REVIEW_FUNCTION_LABEL_MAX_LEN,
  DEMO_CUSTOM_REVIEW_FUNCTIONS_MAX,
  DEMO_REVIEW_FUNCTION_LABELS,
  type DemoCustomReviewFunction,
  type DemoReviewFunction,
  getDemoReviewFunctions,
  toCustomReviewerSlug,
  updateDemoReviewFunctions,
} from "../../../services/demo";
import { errorMessage } from "../../../utils/http";

const QUERY_KEY = ["demo", "review-functions"] as const;

const DEFAULT_LABEL_KEYS = new Set(
  Object.values(DEMO_REVIEW_FUNCTION_LABELS).map((l) => l.toLowerCase()),
);

/**
 * Append a numeric suffix until the candidate id is free. Stops at
 * `_attempt`-many tries so a runaway loop can't lock the UI; in
 * practice the server is also enforcing uniqueness so this is just
 * defensive.
 */
function uniqueSlug(base: string, taken: Set<string>): string {
  if (!taken.has(base)) return base;
  for (let i = 2; i < 100; i += 1) {
    const candidate = `${base}-${i}`;
    if (!taken.has(candidate)) return candidate;
  }
  return `${base}-${Date.now()}`;
}

export function DemoReviewFunctionsCard() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: getDemoReviewFunctions,
  });

  // Local edit buffers so toggling checkboxes / typing labels is
  // instant while the PUT is still in flight. Reconciled from server
  // on initial load and on every successful save.
  const [localEnabled, setLocalEnabled] = useState<DemoReviewFunction[]>([]);
  const [localCustoms, setLocalCustoms] = useState<DemoCustomReviewFunction[]>(
    [],
  );
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingLabel, setEditingLabel] = useState<string>("");
  const [addOpen, setAddOpen] = useState(false);
  const [addLabel, setAddLabel] = useState<string>("");

  useEffect(() => {
    if (data?.enabledReviewFunctions) {
      setLocalEnabled(data.enabledReviewFunctions);
    }
  }, [data?.enabledReviewFunctions]);

  useEffect(() => {
    if (data?.customReviewFunctions) {
      setLocalCustoms(data.customReviewFunctions);
    }
  }, [data?.customReviewFunctions]);

  const mutation = useMutation({
    mutationFn: ({
      enabled,
      customs,
    }: {
      enabled: DemoReviewFunction[];
      customs: DemoCustomReviewFunction[];
    }) => updateDemoReviewFunctions(enabled, customs),
    onSuccess: (result) => {
      setLocalEnabled(result.enabledReviewFunctions);
      setLocalCustoms(result.customReviewFunctions);
      queryClient.setQueryData(QUERY_KEY, {
        success: true,
        enterpriseId: result.enterpriseId,
        enabledReviewFunctions: result.enabledReviewFunctions,
        customReviewFunctions: result.customReviewFunctions,
        defaults: data?.defaults ?? [],
      });
      setErrorMsg(null);
    },
    onError: async (err) => {
      setErrorMsg(await errorMessage(err));
    },
  });

  const allOptions = data?.defaults ?? [
    "brand",
    "agency",
    "regulatory",
    "medical",
    "copy",
  ];

  const takenSlugs = useMemo(
    () => new Set<string>(localCustoms.map((c) => c.id)),
    [localCustoms],
  );
  const takenLabelKeys = useMemo(() => {
    const out = new Set<string>();
    DEFAULT_LABEL_KEYS.forEach((k) => out.add(k));
    localCustoms.forEach((c) => out.add(c.label.toLowerCase()));
    return out;
  }, [localCustoms]);

  const toggleDefault = (fn: DemoReviewFunction) => {
    setErrorMsg(null);
    setLocalEnabled((prev) =>
      prev.includes(fn) ? prev.filter((f) => f !== fn) : [...prev, fn],
    );
  };

  const toggleCustom = (id: string) => {
    setErrorMsg(null);
    setLocalCustoms((prev) =>
      prev.map((c) => (c.id === id ? { ...c, enabled: !c.enabled } : c)),
    );
  };

  const startEdit = (entry: DemoCustomReviewFunction) => {
    setErrorMsg(null);
    setEditingId(entry.id);
    setEditingLabel(entry.label);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingLabel("");
  };

  const saveEdit = (id: string) => {
    const trimmed = editingLabel.trim();
    if (trimmed.length === 0) {
      setErrorMsg("Reviewer label cannot be empty.");
      return;
    }
    if (trimmed.length > DEMO_CUSTOM_REVIEW_FUNCTION_LABEL_MAX_LEN) {
      setErrorMsg(
        `Reviewer label must be ${DEMO_CUSTOM_REVIEW_FUNCTION_LABEL_MAX_LEN} characters or fewer.`,
      );
      return;
    }
    const key = trimmed.toLowerCase();
    if (DEFAULT_LABEL_KEYS.has(key)) {
      setErrorMsg(
        `"${trimmed}" matches a built-in reviewer. Pick a different label.`,
      );
      return;
    }
    const conflict = localCustoms.find(
      (c) => c.id !== id && c.label.toLowerCase() === key,
    );
    if (conflict) {
      setErrorMsg(`"${trimmed}" is already used by another custom reviewer.`);
      return;
    }
    setLocalCustoms((prev) =>
      prev.map((c) => (c.id === id ? { ...c, label: trimmed } : c)),
    );
    setErrorMsg(null);
    setEditingId(null);
    setEditingLabel("");
  };

  const deleteCustom = (id: string) => {
    setErrorMsg(null);
    setLocalCustoms((prev) => prev.filter((c) => c.id !== id));
    if (editingId === id) {
      cancelEdit();
    }
  };

  const onAddConfirm = () => {
    const trimmed = addLabel.trim();
    if (trimmed.length === 0) {
      setErrorMsg("Reviewer label cannot be empty.");
      return;
    }
    if (trimmed.length > DEMO_CUSTOM_REVIEW_FUNCTION_LABEL_MAX_LEN) {
      setErrorMsg(
        `Reviewer label must be ${DEMO_CUSTOM_REVIEW_FUNCTION_LABEL_MAX_LEN} characters or fewer.`,
      );
      return;
    }
    if (localCustoms.length >= DEMO_CUSTOM_REVIEW_FUNCTIONS_MAX) {
      setErrorMsg(
        `You can add up to ${DEMO_CUSTOM_REVIEW_FUNCTIONS_MAX} custom reviewers.`,
      );
      return;
    }
    const key = trimmed.toLowerCase();
    if (takenLabelKeys.has(key)) {
      setErrorMsg(
        `"${trimmed}" is already used by a reviewer. Pick a different label.`,
      );
      return;
    }
    const baseSlug = toCustomReviewerSlug(trimmed);
    const id = uniqueSlug(baseSlug, takenSlugs);
    setLocalCustoms((prev) => [...prev, { id, label: trimmed, enabled: true }]);
    setAddLabel("");
    setAddOpen(false);
    setErrorMsg(null);
  };

  const onSave = () => {
    if (localEnabled.length === 0) {
      setErrorMsg("Pick at least one reviewer before saving.");
      return;
    }
    mutation.mutate({ enabled: localEnabled, customs: localCustoms });
  };

  const onResetDefaults = () => {
    if (data?.defaults) {
      const enabled = data.defaults;
      const customs: DemoCustomReviewFunction[] = [];
      setLocalEnabled(enabled);
      setLocalCustoms(customs);
      cancelEdit();
      setAddOpen(false);
      setAddLabel("");
      mutation.mutate({ enabled, customs });
    }
  };

  const persistedEnabled = data?.enabledReviewFunctions;
  const persistedCustoms = data?.customReviewFunctions;

  const enabledDirty =
    persistedEnabled !== undefined &&
    (localEnabled.length !== persistedEnabled.length ||
      localEnabled.some((fn) => !persistedEnabled.includes(fn)));

  const customsDirty =
    persistedCustoms !== undefined &&
    (localCustoms.length !== persistedCustoms.length ||
      localCustoms.some((c, i) => {
        const prev = persistedCustoms[i];
        return (
          !prev ||
          prev.id !== c.id ||
          prev.label !== c.label ||
          prev.enabled !== c.enabled
        );
      }));

  const dirty = enabledDirty || customsDirty;

  const atCap = localCustoms.length >= DEMO_CUSTOM_REVIEW_FUNCTIONS_MAX;

  return (
    <Card data-testid="settings-card-demo-review-functions">
      <Flex direction="column" gap="3">
        <Flex direction="column" gap="1">
          <Heading as="h2" size="3" weight="bold">
            Which AI reviewers to show
          </Heading>
          <Text size="2" color="gray">
            Pick which AI reviewers appear during the demo. Handy when a buyer
            only cares about regulatory and medical review, for example, and you
            want to hide the others. The five built-in reviewers can be checked
            or unchecked but never renamed or removed. Add your own custom
            reviewers below if you want to attribute scripted answers to a label
            like &quot;Legal&quot; or &quot;Localization&quot;. Click
            &quot;Reset to defaults&quot; to turn all five back on and clear
            every custom reviewer.
          </Text>
        </Flex>
        <Flex
          direction="column"
          gap="2"
          data-testid="demo-review-fn-checkboxes"
        >
          {allOptions.map((fn) => (
            <label
              key={fn}
              className="flex cursor-pointer items-center gap-2"
              data-testid={`demo-review-fn-row-${fn}`}
            >
              <Checkbox
                checked={localEnabled.includes(fn)}
                onCheckedChange={() => toggleDefault(fn)}
                disabled={isLoading || mutation.isPending}
                data-testid={`demo-review-fn-checkbox-${fn}`}
                aria-label={`Toggle ${DEMO_REVIEW_FUNCTION_LABELS[fn]} reviewer`}
              />
              <Text size="2">{DEMO_REVIEW_FUNCTION_LABELS[fn]}</Text>
            </label>
          ))}
        </Flex>

        {localCustoms.length > 0 || addOpen ? <Separator size="4" /> : null}

        {localCustoms.length > 0 ? (
          <Flex direction="column" gap="2" data-testid="demo-review-fn-customs">
            <Text size="1" color="gray">
              Custom reviewers
            </Text>
            {localCustoms.map((entry) => (
              <Flex
                key={entry.id}
                align="center"
                gap="2"
                data-testid={`demo-review-fn-row-${entry.id}`}
              >
                <Checkbox
                  checked={entry.enabled}
                  onCheckedChange={() => toggleCustom(entry.id)}
                  disabled={
                    isLoading || mutation.isPending || editingId === entry.id
                  }
                  data-testid={`demo-review-fn-checkbox-${entry.id}`}
                  aria-label={`Toggle ${entry.label} reviewer`}
                />
                {editingId === entry.id ? (
                  <>
                    <TextField.Root
                      value={editingLabel}
                      onChange={(e) => setEditingLabel(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          saveEdit(entry.id);
                        } else if (e.key === "Escape") {
                          e.preventDefault();
                          cancelEdit();
                        }
                      }}
                      maxLength={DEMO_CUSTOM_REVIEW_FUNCTION_LABEL_MAX_LEN}
                      data-testid={`demo-review-fn-custom-label-input-${entry.id}`}
                      aria-label="Reviewer label"
                      style={{ flex: 1 }}
                    />
                    <Button
                      size="1"
                      onClick={() => saveEdit(entry.id)}
                      data-testid={`demo-review-fn-custom-save-button-${entry.id}`}
                    >
                      Save
                    </Button>
                    <Button
                      size="1"
                      variant="soft"
                      color="gray"
                      onClick={cancelEdit}
                      data-testid={`demo-review-fn-custom-cancel-button-${entry.id}`}
                    >
                      Cancel
                    </Button>
                  </>
                ) : (
                  <>
                    <Text size="2" style={{ flex: 1 }}>
                      {entry.label}
                    </Text>
                    <IconButton
                      size="1"
                      variant="soft"
                      onClick={() => startEdit(entry)}
                      disabled={isLoading || mutation.isPending}
                      data-testid={`demo-review-fn-custom-edit-button-${entry.id}`}
                      aria-label="Edit reviewer"
                    >
                      <Pencil size={14} aria-hidden="true" />
                    </IconButton>
                    <IconButton
                      size="1"
                      variant="soft"
                      color="red"
                      onClick={() => deleteCustom(entry.id)}
                      disabled={isLoading || mutation.isPending}
                      data-testid={`demo-review-fn-custom-delete-button-${entry.id}`}
                      aria-label="Delete reviewer"
                    >
                      <Trash size={14} aria-hidden="true" />
                    </IconButton>
                  </>
                )}
              </Flex>
            ))}
          </Flex>
        ) : null}

        {addOpen ? (
          <Flex align="center" gap="2" data-testid="demo-review-fn-add-form">
            <TextField.Root
              value={addLabel}
              onChange={(e) => setAddLabel(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  onAddConfirm();
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  setAddOpen(false);
                  setAddLabel("");
                  setErrorMsg(null);
                }
              }}
              placeholder="e.g. Legal, Localization, Brand strategist"
              maxLength={DEMO_CUSTOM_REVIEW_FUNCTION_LABEL_MAX_LEN}
              data-testid="demo-review-fn-add-input"
              aria-label="New reviewer label"
              style={{ flex: 1 }}
            />
            <Button
              size="2"
              onClick={onAddConfirm}
              data-testid="demo-review-fn-add-confirm"
            >
              Add
            </Button>
            <Button
              size="2"
              variant="soft"
              color="gray"
              onClick={() => {
                setAddOpen(false);
                setAddLabel("");
                setErrorMsg(null);
              }}
              data-testid="demo-review-fn-add-cancel"
            >
              Cancel
            </Button>
          </Flex>
        ) : (
          <Flex>
            <Button
              variant="soft"
              onClick={() => {
                setAddOpen(true);
                setErrorMsg(null);
              }}
              disabled={isLoading || mutation.isPending || atCap}
              data-testid="demo-review-fn-add-button"
              aria-label="Add custom reviewer"
            >
              <Plus size={14} aria-hidden="true" />
              {atCap
                ? `Custom reviewer limit reached (${DEMO_CUSTOM_REVIEW_FUNCTIONS_MAX})`
                : "+ Add reviewer"}
            </Button>
          </Flex>
        )}

        <Flex align="center" gap="3">
          <Button
            data-testid="demo-review-fn-save"
            onClick={onSave}
            disabled={isLoading || mutation.isPending || !dirty}
          >
            {mutation.isPending ? "Saving…" : "Save"}
          </Button>
          <Button
            data-testid="demo-review-fn-reset"
            variant="soft"
            onClick={onResetDefaults}
            disabled={isLoading || mutation.isPending}
          >
            Reset to defaults
          </Button>
          {mutation.isSuccess && !dirty && !errorMsg ? (
            <Text size="2" color="green">
              Saved.
            </Text>
          ) : null}
          {errorMsg ? (
            <Text
              size="2"
              color="red"
              data-testid="demo-review-fn-error"
              role="alert"
            >
              {errorMsg}
            </Text>
          ) : null}
        </Flex>
      </Flex>
    </Card>
  );
}
